(() => {
  const GLOBAL_KEY = '__jdCollapsibleContentInitialized';

  if (window[GLOBAL_KEY]) return;
  window[GLOBAL_KEY] = true;

  const ITEM_SELECTOR = '.collapsible-content__item';
  const TOGGLE_SELECTOR = '.collapsible-content__toggle';
  const ANSWER_SELECTOR = '.collapsible-content__answer';
  const ITEMS_SELECTOR = '.collapsible-content__items';
  const ANIMATION_DURATION = 300;
  const animations = new WeakMap();
  const reducedMotionQuery = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;
  let uniqueId = 0;

  const prefersReducedMotion = () => Boolean(reducedMotionQuery?.matches);

  const getParts = (item) => {
    if (!item) return {};

    return {
      toggle: item.querySelector(TOGGLE_SELECTOR),
      answer: item.querySelector(ANSWER_SELECTOR),
    };
  };

  const getSiblingItems = (item) => {
    const itemsContainer = item?.closest(ITEMS_SELECTOR);
    if (!itemsContainer) return [];

    return Array.from(itemsContainer.children).filter((child) =>
      child.matches(ITEM_SELECTOR),
    );
  };

  const clearAnimationStyles = (answer) => {
    answer.style.removeProperty('height');
    answer.style.removeProperty('overflow');
  };

  const applyFinalState = (item, toggle, answer, expanded) => {
    item.classList.toggle('active', expanded);
    toggle.setAttribute('aria-expanded', String(expanded));
    answer.setAttribute('aria-hidden', String(!expanded));
    answer.hidden = !expanded;
    answer.style.display = expanded ? 'block' : 'none';
    clearAnimationStyles(answer);
  };

  const setExpanded = (item, expanded, options = {}) => {
    const { animate = true } = options;
    const { toggle, answer } = getParts(item);

    if (!toggle || !answer) return;

    const activeAnimation = animations.get(answer);
    const currentHeight = answer.getBoundingClientRect().height;
    const isRendered = !answer.hidden && answer.style.display !== 'none';

    if (activeAnimation) {
      activeAnimation.cancel();
      animations.delete(answer);
    }

    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
    if (isExpanded === expanded && !activeAnimation) {
      applyFinalState(item, toggle, answer, expanded);
      return;
    }

    item.classList.toggle('active', expanded);
    toggle.setAttribute('aria-expanded', String(expanded));
    answer.setAttribute('aria-hidden', String(!expanded));

    if (
      !animate ||
      prefersReducedMotion() ||
      typeof answer.animate !== 'function'
    ) {
      applyFinalState(item, toggle, answer, expanded);
      return;
    }

    answer.hidden = false;
    answer.style.display = 'block';
    answer.style.overflow = 'hidden';

    const startHeight = isRendered
      ? currentHeight
      : expanded
        ? 0
        : answer.scrollHeight;
    const endHeight = expanded ? answer.scrollHeight : 0;

    const animation = answer.animate(
      [
        { height: `${startHeight}px` },
        { height: `${endHeight}px` },
      ],
      {
        duration: ANIMATION_DURATION,
        easing: 'ease',
      },
    );

    animations.set(answer, animation);

    animation.addEventListener(
      'finish',
      () => {
        if (animations.get(answer) !== animation) return;

        animations.delete(answer);
        applyFinalState(item, toggle, answer, expanded);
      },
      { once: true },
    );

    animation.addEventListener(
      'cancel',
      () => {
        if (animations.get(answer) === animation) {
          animations.delete(answer);
        }
      },
      { once: true },
    );
  };

  const initializeItem = (item) => {
    const { toggle, answer } = getParts(item);
    if (!toggle || !answer) return;

    uniqueId += 1;
    const idBase = `CollapsibleContent-${uniqueId}`;

    toggle.type = 'button';
    toggle.id ||= `${idBase}-toggle`;
    answer.id ||= `${idBase}-answer`;

    toggle.setAttribute('aria-controls', answer.id);
    answer.setAttribute('role', 'region');
    answer.setAttribute('aria-labelledby', toggle.id);

    const expanded = item.classList.contains('active');
    applyFinalState(item, toggle, answer, expanded);
  };

  const initialize = (root = document) => {
    root.querySelectorAll?.(ITEM_SELECTOR).forEach(initializeItem);

    if (root.matches?.(ITEM_SELECTOR)) {
      initializeItem(root);
    }
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest(TOGGLE_SELECTOR);
    if (!toggle) return;

    const item = toggle.closest(ITEM_SELECTOR);
    if (!item || !item.closest('.collapsible-content')) return;

    const expanded = toggle.getAttribute('aria-expanded') === 'true';

    getSiblingItems(item).forEach((sibling) => {
      if (sibling !== item && sibling.classList.contains('active')) {
        setExpanded(sibling, false);
      }
    });

    setExpanded(item, !expanded);
  });

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const item = event.target.closest?.(ITEM_SELECTOR);
    if (!item) return;

    getSiblingItems(item).forEach((sibling) => {
      if (sibling !== item && sibling.classList.contains('active')) {
        setExpanded(sibling, false, { animate: false });
      }
    });

    setExpanded(item, true, { animate: false });
  });

  initialize();
})();
